/**
 * Route Schedule Section JavaScript
 * Displays full-line schedule with 3 docks (departure, transfer, arrival)
 * Supports direction toggle: FROM primary dock / TO primary dock
 */

(function() {
  'use strict';

  // State
  let scheduleData = null;
  let currentDirection = 'forward'; // 'forward' = FROM primary dock, 'reverse' = TO primary dock
  let displayedDays = [];
  let maxDaysAhead = 5;
  let allSlots = [];
  let displayedSlotsCount = 0;
  let hasTransfer = true;
  let colCount = 3;
  const INITIAL_SLOTS_COUNT = 10;
  const LOAD_MORE_SLOTS_COUNT = 10;

  // --- Anchor fallback state (feature: enable_anchor_fallback) ---
  // Anchor routes resolved via fallback, keyed by direction. Populated only when
  // the configured handle is missing; reset at the start of every render.
  let anchorFallbackCache = {};
  // Sub-route objects resolved via fallback, keyed by the handle configured in
  // the customizer. Lets getSubRouteMatchingRoutes() resolve a stale handle
  // without changing its signature.
  let resolvedSubRoutes = {};
  // Human-readable fallback events for the current render. Surfaced in the
  // browser console always, and visually ONLY inside the Shopify theme editor.
  let fallbackNotices = [];

  // Day mapping
  const dayMap = { 0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat' };

  /**
   * Check if a slot is active on a given day.
   * Priority 1: boolean fields (days_booleans) — if any is true, they are authoritative.
   * Priority 2: text field (days_available / days) — fallback for legacy slots.
   * If booleans and text disagree, booleans win.
   */
  function isSlotActiveOnDay(slot, dayAbbr) {
    if (slot.days_booleans) {
      var anyTrue = Object.values(slot.days_booleans).some(function(v) { return v === true; });
      if (anyTrue) {
        return slot.days_booleans[dayAbbr] === true;
      }
    }
    var text = slot.days_available || slot.days || '';
    return text.toLowerCase().includes(dayAbbr);
  }

  // DOM Elements
  let tableHeaderDocks;
  let tableHeaderLabels;
  let tableBody;
  let dirFromBtn;
  let dirToBtn;
  let dirDestination;
  let loadMoreBtn;
  let directionToggle;
  let continuationEl;

  /**
   * Initialize the schedule section
   */
  function init() {
    // Get DOM elements
    tableHeaderDocks = document.getElementById('rs-table-header-docks');
    tableHeaderLabels = document.getElementById('rs-table-header-labels');
    tableBody = document.getElementById('rs-schedule-body');
    dirFromBtn = document.getElementById('rs-dir-from');
    dirToBtn = document.getElementById('rs-dir-to');
    dirDestination = document.getElementById('rs-dir-destination');
    loadMoreBtn = document.getElementById('rs-load-more');
    directionToggle = document.querySelector('.rs-direction-toggle');
    continuationEl = document.getElementById('rs-sched-continuation');

    // Parse schedule data
    const dataEl = document.getElementById('schedule-data');
    if (!dataEl) {
      console.error('Route Schedule: No schedule data found');
      return;
    }

    try {
      scheduleData = JSON.parse(dataEl.textContent);
      const routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];
      scheduleData.routesByHandle = routes.reduce(function(acc, route) {
        if (route && route.handle) {
          acc[route.handle] = route;
        }
        return acc;
      }, {});
    } catch (e) {
      console.error('Route Schedule: Failed to parse schedule data', e);
      return;
    }

    // Setup event listeners
    if (dirFromBtn) {
      dirFromBtn.addEventListener('click', function() {
        setDirection('forward');
      });
    }

    if (dirToBtn) {
      dirToBtn.addEventListener('click', function() {
        setDirection('reverse');
      });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', loadMore);
    }

    // Initial render
    renderSchedule();
  }

  /**
   * Get the current route based on direction (used for reverse route check)
   */
  function getRoute() {
    const handle = currentDirection === 'forward'
      ? scheduleData.route_handle_forward
      : scheduleData.route_handle_reverse;
    if (!handle) return null;
    return scheduleData.routesByHandle[handle] || null;
  }

  // ===========================================================================
  // SMART FALLBACK
  // ---------------------------------------------------------------------------
  // When a route handle saved in the customizer no longer exists as a metaobject
  // (the client deleted or renamed the route), recover by finding another route
  // with the same dock pair instead of rendering an empty table.
  //
  // Hard rules:
  //   - The existing handle lookup always runs first and always wins.
  //   - None of this code executes when the handle resolves.
  //   - The whole path is gated behind the enable_anchor_fallback setting.
  // ===========================================================================

  /**
   * Is the fallback turned on for this section?
   */
  function fallbackEnabled() {
    return !!(scheduleData && scheduleData.enable_anchor_fallback === true);
  }

  /**
   * A dock setting counts as configured only when it holds a real dock name.
   * 'none' is the schema sentinel for "not set".
   */
  function isDockSet(dock) {
    return typeof dock === 'string' && dock !== '' && dock !== 'none';
  }

  /**
   * Compare two dock names tolerantly (case and surrounding whitespace).
   * Used ONLY to locate a replacement route. Everything downstream keeps
   * comparing the exact dock strings carried by the route object we return,
   * so getMatchingRoutes() strict matching is unaffected.
   */
  function docksEqual(a, b) {
    if (typeof a !== 'string' || typeof b !== 'string') return false;
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  /**
   * Does this route run today? Uses exactly the same date-range and
   * day-of-week rules the renderer already applies to slots.
   */
  function isRouteActiveToday(route) {
    if (!route || !Array.isArray(route.time_slots)) return false;
    var today = new Date();
    if (!isDateInRange(route, today)) return false;
    var dayAbbr = dayMap[today.getDay()];
    return route.time_slots.some(function(slot) {
      return isSlotActiveOnDay(slot, dayAbbr);
    });
  }

  /**
   * Find a replacement route for a dock pair.
   * Tie-breaker (approved strategy):
   *   1. candidates = routes matching departure_dock + arrival_dock,
   *      kept in scheduleData.routes order
   *   2. prefer the first candidate with slots active TODAY
   *   3. otherwise the first candidate in scheduleData.routes order
   * Returns null when the pair is unset or nothing matches.
   */
  function findFallbackRoute(departureDock, arrivalDock) {
    if (!isDockSet(departureDock) || !isDockSet(arrivalDock)) return null;

    var routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];
    var candidates = routes.filter(function(route) {
      return docksEqual(route.departure_dock, departureDock) &&
             docksEqual(route.arrival_dock, arrivalDock);
    });
    if (candidates.length === 0) return null;

    for (var i = 0; i < candidates.length; i++) {
      if (isRouteActiveToday(candidates[i])) return candidates[i];
    }
    return candidates[0];
  }

  /**
   * Record a fallback event: console for everyone, admin banner for the editor.
   */
  function noteFallback(message) {
    console.warn('[Route Schedule] ' + message);
    fallbackNotices.push(message);
  }

  /**
   * Intended dock pair for a direction, from the section settings.
   * forward = primary_dock -> destination_dock; reverse is the mirror image.
   */
  function getConfiguredDockPairFor(direction) {
    var primary = scheduleData.primary_dock;
    var destination = scheduleData.destination_dock;
    if (!isDockSet(primary) || !isDockSet(destination)) return null;
    return direction === 'forward'
      ? { departure_dock: primary, arrival_dock: destination }
      : { departure_dock: destination, arrival_dock: primary };
  }

  /**
   * Resolve the anchor route for an explicit direction.
   * Step 1 handle lookup -> step 2 dock-pair fallback -> step 3 null.
   */
  function resolveAnchorRouteFor(direction) {
    if (!scheduleData) return null;

    var handle = direction === 'forward'
      ? scheduleData.route_handle_forward
      : scheduleData.route_handle_reverse;
    if (!handle) return null;

    // 1. Existing behavior, untouched. Wins whenever the handle exists.
    var byHandle = scheduleData.routesByHandle
      ? scheduleData.routesByHandle[handle]
      : null;
    if (byHandle) return byHandle;

    // 2. Fallback path — only from here on.
    if (!fallbackEnabled()) return null;

    if (Object.prototype.hasOwnProperty.call(anchorFallbackCache, direction)) {
      return anchorFallbackCache[direction];
    }

    var label = 'Anchor route (' + direction + ')';
    var pair = getConfiguredDockPairFor(direction);
    var resolved = null;

    if (!pair) {
      noteFallback(label + ': saved route "' + handle + '" no longer exists, and ' +
        'no fallback dock pair is configured. Set "Primary Dock Name" and ' +
        '"Destination Dock" in this section to enable automatic recovery.');
    } else {
      resolved = findFallbackRoute(pair.departure_dock, pair.arrival_dock);
      if (resolved) {
        noteFallback(label + ': saved route "' + handle + '" no longer exists. ' +
          'Now using "' + resolved.handle + '" (' + pair.departure_dock +
          ' \u2192 ' + pair.arrival_dock + ').');
      } else {
        noteFallback(label + ': saved route "' + handle + '" no longer exists, ' +
          'and no other route runs ' + pair.departure_dock + ' \u2192 ' +
          pair.arrival_dock + '.');
      }
    }

    anchorFallbackCache[direction] = resolved;
    return resolved;
  }

  /**
   * Resolve a sub-route for the current direction.
   * Same three steps as the anchor, using the block's own dock settings.
   */
  function resolveSubRoute(handle, subRouteConfig) {
    // 1. Existing behavior, untouched.
    var byHandle = scheduleData.routesByHandle
      ? scheduleData.routesByHandle[handle]
      : null;
    if (byHandle) return byHandle;

    // 2. Fallback path — only from here on.
    if (!fallbackEnabled()) return null;

    if (Object.prototype.hasOwnProperty.call(resolvedSubRoutes, handle)) {
      return resolvedSubRoutes[handle];
    }

    // The block stores the pair in forward orientation; reverse mirrors it.
    var depDock = currentDirection === 'forward'
      ? subRouteConfig.fallback_departure_dock
      : subRouteConfig.fallback_arrival_dock;
    var arrDock = currentDirection === 'forward'
      ? subRouteConfig.fallback_arrival_dock
      : subRouteConfig.fallback_departure_dock;

    var label = 'Sub-route (' + currentDirection + ')';
    var resolved = null;

    if (!isDockSet(depDock) || !isDockSet(arrDock)) {
      noteFallback(label + ': saved route "' + handle + '" no longer exists, and ' +
        'this Sub-Route block has no fallback docks configured. This block is ' +
        'skipped; the rest of the schedule is unaffected.');
    } else {
      resolved = findFallbackRoute(depDock, arrDock);
      if (resolved) {
        noteFallback(label + ': saved route "' + handle + '" no longer exists. ' +
          'Now using "' + resolved.handle + '" (' + depDock + ' \u2192 ' +
          arrDock + ').');
      } else {
        noteFallback(label + ': saved route "' + handle + '" no longer exists, ' +
          'and no other route runs ' + depDock + ' \u2192 ' + arrDock +
          '. This block is skipped; the rest of the schedule is unaffected.');
      }
    }

    resolvedSubRoutes[handle] = resolved;
    return resolved;
  }

  /**
   * Is the page being viewed inside the Shopify theme editor?
   */
  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Draw the admin-only fallback banner.
   * Returns immediately for regular visitors — isDesignMode() is checked before
   * anything is inserted into the DOM, so the banner cannot reach the public site.
   */
  function renderFallbackNotices() {
    var existing = document.getElementById('rs-fallback-notice');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    if (!isDesignMode()) return;
    if (fallbackNotices.length === 0) return;

    var container = document.querySelector('.rs-schedule-container');
    if (!container) return;

    var box = document.createElement('div');
    box.id = 'rs-fallback-notice';
    box.setAttribute('data-rs-admin-only', 'true');
    box.style.cssText = 'margin:16px 0 0;padding:12px 16px;border:1px solid #f5a623;' +
      'border-left-width:4px;background:#fff8e8;color:#3d3d3d;font-size:13px;' +
      'line-height:1.5;text-align:left;border-radius:4px;';

    var html = '<strong>Schedule Table \u2014 admin notice (theme editor only)</strong>' +
      '<ul style="margin:8px 0 0;padding-left:18px;">';
    fallbackNotices.forEach(function(message) {
      html += '<li>' + escapeHtml(message) + '</li>';
    });
    html += '</ul>';
    box.innerHTML = html;

    container.appendChild(box);
  }

  /**
   * Get the anchor dock pair from the resolved anchor route.
   * This determines which dock pair to filter all routes by.
   */
  function getAnchorDockPair() {
    const anchorRoute = getAnchorRoute();
    if (!anchorRoute) return null;

    return {
      departure_dock: anchorRoute.departure_dock,
      arrival_dock: anchorRoute.arrival_dock
    };
  }

  /**
   * Get the anchor route for header rendering (dock names).
   */
  function getAnchorRoute() {
    return resolveAnchorRouteFor(currentDirection);
  }

  /**
   * First non-empty transfer_dock across a route's slots.
   * Returns null when the route is missing or carries none.
   */
  function firstTransferDock(route) {
    if (!route || !route.time_slots) return null;
    for (var i = 0; i < route.time_slots.length; i++) {
      var td = route.time_slots[i].transfer_dock;
      if (td && td !== '') return td;
    }
    return null;
  }

  /**
   * Get the transfer dock name from anchor route slots.
   *
   * When the anchor carries no transfer dock on any slot (an incomplete route
   * record), borrow the name from the opposite direction's anchor, which serves
   * the same dock pair mirrored. Affects the middle column header and the
   * sub-route column mapping only — no slot data is read or written.
   * Never overrides a value the anchor already has; gated behind
   * enable_anchor_fallback, so unchecking it restores the legacy behavior.
   */
  function getAnchorTransferDock() {
    var td = firstTransferDock(getAnchorRoute());
    if (td) return td;

    if (!fallbackEnabled()) return null;

    var mirror = currentDirection === 'forward' ? 'reverse' : 'forward';
    return firstTransferDock(resolveAnchorRouteFor(mirror));
  }

  /**
   * Build a column mapping for a sub-route relative to the anchor route's docks.
   * Returns an object describing which anchor column each sub-route dock maps to,
   * or null if the sub-route handle is not found or docks don't match.
   */
  function buildColumnMapping(subRouteConfig) {
    var handle = currentDirection === 'forward'
      ? subRouteConfig.route_handle_forward
      : subRouteConfig.route_handle_reverse;
    if (!handle) return null;

    var subRoute = resolveSubRoute(handle, subRouteConfig);
    if (!subRoute) return null;

    var anchorRoute = getAnchorRoute();
    if (!anchorRoute) return null;

    // Build ordered array of anchor dock names: [departure, transfer?, arrival]
    var anchorDocks = [anchorRoute.departure_dock];
    var transferDock = getAnchorTransferDock();
    if (transferDock) anchorDocks.push(transferDock);
    anchorDocks.push(anchorRoute.arrival_dock);

    // Map sub-route docks to anchor column indices
    var depCol = anchorDocks.indexOf(subRoute.departure_dock);
    var arrCol = anchorDocks.indexOf(subRoute.arrival_dock);

    // If neither dock matches any anchor dock, skip this sub-route
    if (depCol === -1 && arrCol === -1) return null;

    return {
      handle: handle,
      subRoute: subRoute,
      departureDockCol: depCol,
      arrivalDockCol: arrCol,
      anchorDocks: anchorDocks,
      missingDockText: subRouteConfig.missing_dock_text || 'Does Not Stop'
    };
  }

  /**
   * Get all routes matching a sub-route's dock pair for a specific date.
   * Applies the same date range filtering and override priority logic.
   */
  function getSubRouteMatchingRoutes(handle, date) {
    // Signature unchanged. resolvedSubRoutes is populated by buildColumnMapping
    // earlier in the same render, so a handle recovered via fallback resolves here too.
    var subRoute = scheduleData.routesByHandle[handle] ||
      resolvedSubRoutes[handle] || null;
    if (!subRoute) return [];

    var routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];

    var matchingRoutes = routes.filter(function(route) {
      if (route.departure_dock !== subRoute.departure_dock) return false;
      if (route.arrival_dock !== subRoute.arrival_dock) return false;
      if (!isDateInRange(route, date)) return false;
      return true;
    });

    var overrideRoutes = matchingRoutes.filter(function(route) {
      return route.override === true && route.schedule_type === 'seasonal';
    });
    if (overrideRoutes.length > 0) matchingRoutes = overrideRoutes;

    return matchingRoutes;
  }

  /**
   * Get all routes matching the anchor dock pair for a specific date.
   * Applies date range filtering and override priority logic.
   */
  function getMatchingRoutes(date) {
    const anchorDockPair = getAnchorDockPair();
    if (!anchorDockPair) return [];

    const routes = Array.isArray(scheduleData.routes) ? scheduleData.routes : [];

    // Filter routes by dock pair and date range
    let matchingRoutes = routes.filter(function(route) {
      if (route.departure_dock !== anchorDockPair.departure_dock) return false;
      if (route.arrival_dock !== anchorDockPair.arrival_dock) return false;
      if (!isDateInRange(route, date)) return false;
      return true;
    });

    // Apply override priority: seasonal override routes take precedence
    const overrideRoutes = matchingRoutes.filter(function(route) {
      return route.override === true && route.schedule_type === 'seasonal';
    });

    if (overrideRoutes.length > 0) {
      matchingRoutes = overrideRoutes;
    }

    return matchingRoutes;
  }

  /**
   * Generate a unique key for a slot to prevent duplicates.
   * Includes transfer_arrival to distinguish slots with different transfer timings.
   */
  function getSlotKey(slot) {
    return [
      slot.departure_time || '',
      slot.arrival_time || '',
      slot.transfer_arrival || ''
    ].join('|');
  }

  /**
   * Convert AM/PM to lowercase in time string
   */
  function formatTimeLowercase(timeStr) {
    if (!timeStr) return '-';
    return timeStr.replace(/AM/g, 'am').replace(/PM/g, 'pm');
  }

  /**
   * Parse time string to minutes since midnight
   */
  function parseTime(timeStr) {
    if (!timeStr) return 0;
    const parts = timeStr.trim().split(' ');
    if (parts.length < 2) return 0;

    const [time, period] = parts;
    let [hours, minutes] = time.split(':').map(Number);

    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  /**
   * Normalize a user-entered time string to minutes since midnight.
   * Returns null if the string is unparseable — callers treat null as "do not dedupe".
   * Handles: varied spacing, casing, missing leading zeros, single-digit minutes.
   * Does NOT guess AM/PM — if missing, returns null (safe fallback).
   */
  function normalizeTime(str) {
    if (!str || typeof str !== 'string') return null;

    // Trim and collapse whitespace
    var s = str.trim().replace(/\s+/g, ' ').toLowerCase();

    // Extract am/pm suffix
    var period = null;
    var match = s.match(/\s*(am|pm)\s*$/);
    if (match) {
      period = match[1];
      s = s.slice(0, match.index).trim();
    } else {
      // No AM/PM found — refuse to guess
      return null;
    }

    // Must have a colon
    var colonIdx = s.indexOf(':');
    if (colonIdx === -1) return null;

    var hourStr = s.slice(0, colonIdx);
    var minStr = s.slice(colonIdx + 1);

    // Pad single-digit minutes (e.g. "5" → "05")
    if (minStr.length === 1) {
      minStr = '0' + minStr;
    }

    var hours = parseInt(hourStr, 10);
    var minutes = parseInt(minStr, 10);

    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours < 1 || hours > 12) return null;
    if (minutes < 0 || minutes > 59) return null;

    // Convert to 24-hour
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  /**
   * Check if a time has already passed today
   */
  function isTimePassed(timeStr) {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return parseTime(timeStr) < nowMinutes;
  }

  /**
   * Check if a date is today
   */
  function isToday(date) {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  }

  /**
   * Check if date is within route's date range
   */
  function isDateInRange(route, date) {
    if (!route.start_date || !route.end_date) return true;

    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startDate = new Date(route.start_date + 'T00:00:00');
    const endDate = new Date(route.end_date + 'T23:59:59');

    return checkDate >= startDate && checkDate <= endDate;
  }

  /**
   * Get slots for a specific date from multiple routes.
   * Merges slots from all matching routes, deduplicates, and preserves all transfer fields.
   */
  function getSlotsForDate(routes, date) {
    if (!Array.isArray(routes) || routes.length === 0) return [];

    const dayOfWeek = dayMap[date.getDay()];
    const checkToday = isToday(date);
    const seenKeys = {};
    let allSlots = [];

    // Collect slots from all routes
    routes.forEach(function(route) {
      if (!route || !route.time_slots) return;
      if (!isDateInRange(route, date)) return;

      route.time_slots.forEach(function(slot) {
        // Check if this day is available (booleans primary, text fallback)
        if (!isSlotActiveOnDay(slot, dayOfWeek)) return;

        // If today, filter out passed times
        if (checkToday && isTimePassed(slot.departure_time)) {
          return;
        }

        // Deduplicate by composite key (preserves slots with different transfer timings)
        const key = getSlotKey(slot);
        if (seenKeys[key]) return;
        seenKeys[key] = true;

        // Push the full slot with all transfer fields preserved
        allSlots.push(slot);
      });
    });

    // Sort by departure time
    allSlots.sort(function(a, b) {
      return parseTime(a.departure_time) - parseTime(b.departure_time);
    });

    return allSlots;
  }

  /**
   * Format date for display
   */
  function formatDate(date, includeToday) {
    const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    const formatted = date.toLocaleDateString('en-US', options);

    if (includeToday && isToday(date)) {
      return 'Today - ' + formatted;
    }

    return formatted;
  }

  /**
   * Set the direction and re-render
   */
  function setDirection(direction) {
    currentDirection = direction;

    // Update button states
    if (dirFromBtn && dirToBtn) {
      dirFromBtn.classList.toggle('active', direction === 'forward');
      dirToBtn.classList.toggle('active', direction === 'reverse');
    }

    // Re-render
    renderSchedule();
  }

  /**
   * Find the dock every row in the table has in common, so the sort can compare
   * like with like.
   *
   * A sub-route row that skips the first dock renders "Does Not Stop" there and
   * carries its own departure_time in a later column. Ordering on departure_time
   * alone therefore compares an anchor's first-dock time against a sub-route's
   * middle-dock time, and no column reads chronologically.
   *
   * Derived from the sub-route column mapping, never from the direction:
   *   - every sub-route starts at the anchor's first dock -> 'origin'
   *   - every sub-route ends at the anchor's last dock    -> 'destination'
   * Anything else returns null and the legacy departure_time ordering is kept.
   */
  function getSharedDockAnchorPoint(mappings) {
    if (!mappings || mappings.length === 0) return 'origin';

    var lastCol = mappings[0].anchorDocks.length - 1;

    var sharesOrigin = mappings.every(function(m) {
      return m.departureDockCol === 0;
    });
    if (sharesOrigin) return 'origin';

    var sharesDestination = mappings.every(function(m) {
      return m.arrivalDockCol === lastCol;
    });
    if (sharesDestination) return 'destination';

    return null;
  }

  /**
   * The time a row shows in a given anchor column, or '' when that column
   * renders "Does Not Stop" for a sub-route or carries no value at all.
   * Mirrors the column placement done by renderNextSlots().
   */
  function slotTimeInColumn(item, col, lastCol) {
    var mapping = item._subRouteMapping;
    if (mapping) {
      if (col === mapping.departureDockCol) return item.slot.departure_time;
      if (col === mapping.arrivalDockCol) return item.slot.arrival_time;
      return '';
    }
    if (col === 0) return item.slot.departure_time;
    if (col === lastCol) return item.slot.arrival_time;
    return item.slot.transfer_departure;
  }

  /**
   * Gather all slots from today onwards using dock-pair filtering.
   * Automatically finds all routes matching the anchor dock pair for each date.
   */
  function gatherAllSlots() {
    allSlots = [];

    // Check if we have an anchor dock pair
    const anchorDockPair = getAnchorDockPair();
    if (!anchorDockPair) return allSlots;

    // Pre-compute sub-route column mappings (once per render)
    resolvedSubRoutes = {};
    var subRoutes = scheduleData.sub_routes || [];
    var subRouteMappings = [];
    subRoutes.forEach(function(sr) {
      var mapping = buildColumnMapping(sr);
      if (mapping) subRouteMappings.push(mapping);
    });

    const today = new Date();
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + maxDaysAhead);

    let checkDate = new Date(today);
    while (checkDate <= maxDate) {
      // Get all matching routes for this specific date
      const matchingRoutes = getMatchingRoutes(checkDate);

      // Get merged slots from all matching routes
      const daySlots = getSlotsForDate(matchingRoutes, checkDate);

      daySlots.forEach(function(slot) {
        allSlots.push({
          date: new Date(checkDate),
          slot: slot,
          _subRouteMapping: null
        });
      });

      // Sub-route slots
      subRouteMappings.forEach(function(mapping) {
        var subMatchingRoutes = getSubRouteMatchingRoutes(mapping.handle, checkDate);
        var subDaySlots = getSlotsForDate(subMatchingRoutes, checkDate);
        subDaySlots.forEach(function(slot) {
          allSlots.push({
            date: new Date(checkDate),
            slot: slot,
            _subRouteMapping: mapping
          });
        });
      });

      checkDate.setDate(checkDate.getDate() + 1);
    }

    // Sort all slots: date first, then the time at the dock every row shares.
    var sharedPoint = getSharedDockAnchorPoint(subRouteMappings);
    var lastCol = subRouteMappings.length
      ? subRouteMappings[0].anchorDocks.length - 1
      : 1;
    var refCol = sharedPoint === 'destination' ? lastCol : 0;

    // Columns compared, in travel order, once the shared dock ties.
    var tieCols = [];
    for (var c = 0; c <= lastCol; c++) {
      if (c !== refCol) tieCols.push(c);
    }

    allSlots.sort(function(a, b) {
      var dateDiff = a.date.getTime() - b.date.getTime();
      if (dateDiff !== 0) return dateDiff;

      if (sharedPoint === null) {
        return parseTime(a.slot.departure_time) - parseTime(b.slot.departure_time);
      }

      var diff = parseTime(slotTimeInColumn(a, refCol, lastCol)) -
                 parseTime(slotTimeInColumn(b, refCol, lastCol));
      if (diff !== 0) return diff;

      // Tie: walk the remaining columns in travel order. A column where either
      // row has no time is skipped, so an anchor still awaiting enrichAnchor()
      // (empty transfer_departure) never jumps ahead of its tie group.
      for (var i = 0; i < tieCols.length; i++) {
        var timeA = slotTimeInColumn(a, tieCols[i], lastCol);
        var timeB = slotTimeInColumn(b, tieCols[i], lastCol);
        if (!timeA || !timeB) continue;
        diff = parseTime(timeA) - parseTime(timeB);
        if (diff !== 0) return diff;
      }

      return 0;
    });

    // Remove sub-route slots that duplicate an anchor slot
    allSlots = deduplicateSubRouteSlots(allSlots);

    return allSlots;
  }

  /**
   * Remove sub-route slots that are duplicates of anchor slots.
   * A sub-route slot is a duplicate when its departure_time AND its arrival_time
   * (which maps to the anchor's transfer_departure column) both match an anchor
   * slot on the same date. If any time is unparseable, the slot is kept (safe).
   */
  function deduplicateSubRouteSlots(slots) {
    if (!scheduleData.hide_duplicate_subroute_slots) return slots;

    // Extract only digits and colon from a time string (e.g. "10:00 am" → "10:00")
    function extractDigits(str) {
      if (!str) return '';
      return str.replace(/[^0-9:]/g, '');
    }

    function norm(str) {
      var n = normalizeTime(str);
      return n !== null ? n : extractDigits(str);
    }

    // Table-level 3-column gate. Enrichment is only meaningful when a middle
    // column actually renders. Mirrors the hasTransfer test at line 639.
    var tableHasTransfer = slots.some(function(item) {
      return item._subRouteMapping === null &&
             item.slot.transfer_dock && item.slot.transfer_dock !== '';
    });

    // Temporal coherence on normalized minutes. Returns true only when all three
    // normalize via strict normalizeTime AND depMin < midMin < arrMin (strict).
    function coherent(depStr, midStr, arrStr) {
      var d = normalizeTime(depStr);
      var m = normalizeTime(midStr);
      var a = normalizeTime(arrStr);
      if (d === null || m === null || a === null) return false;
      return d < m && m < a;
    }

    // Mutation-safe enrichment (Option A): replace the ephemeral wrapper's slot
    // reference with a shallow clone carrying the new middle time. The shared
    // scheduleData slot object is never written to.
    function enrichAnchor(anchorWrapper, middleTimeStr) {
      var clone = Object.assign({}, anchorWrapper.slot);
      clone.transfer_departure = middleTimeStr;
      anchorWrapper.slot = clone;
    }

    // Collect anchor items per date
    var anchorsByDate = {};
    slots.forEach(function(item) {
      if (item._subRouteMapping !== null) return;
      var ds = item.date.toDateString();
      if (!anchorsByDate[ds]) anchorsByDate[ds] = [];
      anchorsByDate[ds].push(item);
    });

    // Build lookup: date+departureTime → anchor item (forward-style dedup)
    var anchorIndex = {};
    slots.forEach(function(item) {
      if (item._subRouteMapping !== null) return;
      var key = item.date.toDateString() + '|' + norm(item.slot.departure_time);
      anchorIndex[key] = item;
    });

    return slots.filter(function(item) {
      if (item._subRouteMapping === null) return true;

      var ds = item.date.toDateString();
      var subDepNorm = norm(item.slot.departure_time);
      var subArrNorm = norm(item.slot.arrival_time);

      // Strategy 1 (forward): sub-route departure matches anchor departure
      var key = ds + '|' + subDepNorm;
      var anchorItem = anchorIndex[key];
      if (anchorItem) {
        var anchorTransferNorm = norm(anchorItem.slot.transfer_departure);
        if (anchorTransferNorm && subArrNorm && String(subArrNorm) === String(anchorTransferNorm)) {
          return false; // duplicate — drop
        }
        // Anchor middle is empty → try coherent merge (sub.arrival is the candidate middle)
        else if (tableHasTransfer && !anchorTransferNorm) {
          if (coherent(anchorItem.slot.departure_time, item.slot.arrival_time, anchorItem.slot.arrival_time)) {
            enrichAnchor(anchorItem, item.slot.arrival_time);
            return false; // drop the now-merged sub
          }
          // coherence failed → fall through → keep both
        }
      }

      // Strategy 2 (reverse): sub-route departure matches anchor's transfer_departure
      // and sub-route arrival matches anchor's arrival
      var anchors = anchorsByDate[ds] || [];
      for (var i = 0; i < anchors.length; i++) {
        var a = anchors[i];
        var aTransferNorm = norm(a.slot.transfer_departure);
        if (aTransferNorm && String(subDepNorm) === String(aTransferNorm)) {
          var aArrNorm = norm(a.slot.arrival_time);
          if (aArrNorm && String(subArrNorm) === String(aArrNorm)) {
            return false; // duplicate — drop
          }
        }
        // This anchor's middle is empty → try coherent reverse merge, matching on
        // the shared ARRIVAL endpoint (sub.departure is the candidate middle).
        else if (tableHasTransfer && !aTransferNorm) {
          var aArrNorm2 = norm(a.slot.arrival_time);
          if (aArrNorm2 && subArrNorm && String(subArrNorm) === String(aArrNorm2)) {
            if (coherent(a.slot.departure_time, item.slot.departure_time, a.slot.arrival_time)) {
              enrichAnchor(a, item.slot.departure_time);
              return false; // drop the now-merged sub
            }
          }
        }
      }

      return true;
    });
  }

  /**
   * Render the schedule table
   */
  function renderSchedule() {
    // Fresh fallback state for this render.
    anchorFallbackCache = {};
    fallbackNotices = [];

    const hasReverseRoute = !!(scheduleData && resolveAnchorRouteFor('reverse'));

    if (!hasReverseRoute) {
      currentDirection = 'forward';
      if (directionToggle) directionToggle.style.display = 'none';
      if (dirFromBtn && dirToBtn) {
        dirFromBtn.classList.add('active');
        dirToBtn.classList.remove('active');
      }
    } else if (directionToggle) {
      directionToggle.style.display = '';
    }

    // Get anchor route for dock names in header
    const anchorRoute = getAnchorRoute();

    if (!anchorRoute) {
      colCount = 3;
      tableHeaderDocks.innerHTML = '';
      tableHeaderLabels.innerHTML = '';
      tableBody.innerHTML = '<tr><td colspan="' + colCount + '" class="rs-no-departures">No schedule data available.</td></tr>';
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      updateContinuation(null);
      renderFallbackNotices();
      return;
    }

    // Update destination label - keep primary dock fixed
    // The primary_dock is set in Liquid and doesn't change with direction
    // dirDestination is already set in HTML from Liquid

    // Reset state
    displayedDays = [];
    displayedSlotsCount = 0;

    // Clear table
    tableBody.innerHTML = '';

    // Gather all available slots using dock-pair filtering (no parameter needed)
    gatherAllSlots();

    // Determine if anchor route has a transfer dock (drives 2 vs 3 column mode)
    // Sub-routes do not force column mode changes — they map to existing columns
    hasTransfer = allSlots.some(function(item) {
      return item._subRouteMapping === null &&
             item.slot.transfer_dock && item.slot.transfer_dock !== '';
    });
    colCount = hasTransfer ? 3 : 2;

    const table = document.getElementById('rs-schedule-table');
    if (table) {
      table.classList.toggle('rs-cols-3', hasTransfer);
      table.classList.toggle('rs-cols-2', !hasTransfer);
    }

    if (allSlots.length === 0) {
      tableBody.innerHTML = '<tr><td colspan="' + colCount + '" class="rs-no-departures">No departures scheduled for the upcoming days.</td></tr>';
      if (loadMoreBtn) loadMoreBtn.style.display = 'none';
      updateContinuation(anchorRoute);
      renderFallbackNotices();
      return;
    }

    // Render header using anchor route dock names (decoupled from slot order)
    renderTableHeader(anchorRoute);

    // Render initial slots (first 10)
    renderNextSlots(INITIAL_SLOTS_COUNT);

    // Show/hide load more button
    updateLoadMoreButton();
    updateContinuation(anchorRoute);
    renderFallbackNotices();
  }

  /**
   * Render table header with dock names
   * Always shows: Departing (left) → Arriving (right) regardless of direction
   * The route data itself determines which docks are departure/arrival
   */
  function renderTableHeader(route) {
    const headerBgColor = scheduleData.header_bg_color || '#F5A623';

    // Get transfer dock name from anchor route slots (not from first displayed slot)
    const transferDock = getAnchorTransferDock() || 'Transfer';

    // Get dock names - these come from the selected route (forward or reverse)
    // so they're already correct for the chosen direction
    const departureDock = route.departure_dock || 'Departure';
    const arrivalDock = route.arrival_dock || 'Arrival';

    if (hasTransfer) {
      // 3 columns: Departure | Transfer | Arrival (always left to right)
      tableHeaderDocks.innerHTML =
        '<th style="background: ' + headerBgColor + ';">' + departureDock + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + (transferDock || 'Transfer') + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + arrivalDock + '</th>';

      tableHeaderLabels.innerHTML =
        '<th>Departing</th>' +
        '<th>Departing</th>' +
        '<th>Arriving</th>';
    } else {
      // 2 columns: Departure | Arrival (always left to right)
      tableHeaderDocks.innerHTML =
        '<th style="background: ' + headerBgColor + ';">' + departureDock + '</th>' +
        '<th style="background: ' + headerBgColor + ';">' + arrivalDock + '</th>';

      tableHeaderLabels.innerHTML =
        '<th>Departing</th>' +
        '<th>Arriving</th>';
    }
  }

  /**
   * Render a date separator row
   */
  function renderDateRow(date, isFirst) {
    const row = document.createElement('tr');
    row.className = 'rs-date-separator';
    row.innerHTML = '<td colspan="' + colCount + '">' + formatDate(date, isFirst) + '</td>';
    tableBody.appendChild(row);
  }

  /**
   * Render time slots (legacy function, kept for compatibility)
   * Always shows: departure_time (left) → arrival_time (right)
   */
  function renderSlots(slots) {
    slots.forEach(function(slot) {
      const row = document.createElement('tr');
      if (hasTransfer) {
        row.innerHTML =
          '<td>' + formatTimeLowercase(slot.departure_time) + '</td>' +
          '<td>' + formatTimeLowercase(slot.transfer_departure) + '</td>' +
          '<td>' + formatTimeLowercase(slot.arrival_time) + '</td>';
      } else {
        row.innerHTML =
          '<td>' + formatTimeLowercase(slot.departure_time) + '</td>' +
          '<td>' + formatTimeLowercase(slot.arrival_time) + '</td>';
      }
      tableBody.appendChild(row);
    });
  }

  /**
   * Render next N slots from allSlots array
   * Always shows: departure_time (left) → arrival_time (right)
   */
  function renderNextSlots(count) {
    const startIdx = displayedSlotsCount;
    const endIdx = Math.min(startIdx + count, allSlots.length);
    let lastDateStr = null;

    for (let i = startIdx; i < endIdx; i++) {
      const item = allSlots[i];
      const dateStr = item.date.toDateString();

      // Add date separator if new day
      if (dateStr !== lastDateStr) {
        const isFirst = displayedDays.length === 0;
        renderDateRow(item.date, isFirst);
        displayedDays.push(dateStr);
        lastDateStr = dateStr;
      }

      // Render the slot
      const row = document.createElement('tr');

      if (item._subRouteMapping) {
        // SUB-ROUTE SLOT — map departure/arrival to correct columns
        var m = item._subRouteMapping;
        if (hasTransfer) {
          // 3-column mode
          var cells = [];
          for (var c = 0; c < 3; c++) {
            if (c === m.departureDockCol) {
              cells.push(formatTimeLowercase(item.slot.departure_time));
            } else if (c === m.arrivalDockCol) {
              cells.push(formatTimeLowercase(item.slot.arrival_time));
            } else {
              cells.push('<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
            }
          }
          row.innerHTML = '<td>' + cells[0] + '</td><td>' + cells[1] + '</td><td>' + cells[2] + '</td>';
        } else {
          // 2-column mode
          var col0 = m.departureDockCol === 0
            ? formatTimeLowercase(item.slot.departure_time)
            : (m.arrivalDockCol === 0 ? formatTimeLowercase(item.slot.arrival_time)
              : '<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
          var col1 = m.arrivalDockCol === 1
            ? formatTimeLowercase(item.slot.arrival_time)
            : (m.departureDockCol === 1 ? formatTimeLowercase(item.slot.departure_time)
              : '<span class="rs-does-not-stop">' + m.missingDockText + '</span>');
          row.innerHTML = '<td>' + col0 + '</td><td>' + col1 + '</td>';
        }
      } else {
        // ANCHOR SLOT — existing logic unchanged
        if (hasTransfer) {
          var middleContent = formatTimeLowercase(item.slot.transfer_departure);
          if (item.slot.is_transfer === true) {
            middleContent += '<br><small class="rs-transfer-label">Transfer — change boats here</small>';
          }
          row.innerHTML =
            '<td>' + formatTimeLowercase(item.slot.departure_time) + '</td>' +
            '<td>' + middleContent + '</td>' +
            '<td>' + formatTimeLowercase(item.slot.arrival_time) + '</td>';
        } else {
          row.innerHTML =
            '<td>' + formatTimeLowercase(item.slot.departure_time) + '</td>' +
            '<td>' + formatTimeLowercase(item.slot.arrival_time) + '</td>';
        }
      }
      tableBody.appendChild(row);

      displayedSlotsCount++;
    }
  }

  /**
   * Load more slots
   */
  function loadMore() {
    if (displayedSlotsCount >= allSlots.length) {
      updateLoadMoreButton();
      return;
    }

    renderNextSlots(LOAD_MORE_SLOTS_COUNT);
    updateLoadMoreButton();
  }

  /**
   * Update load more button visibility
   */
  function updateLoadMoreButton() {
    if (!loadMoreBtn) return;

    const hasMore = displayedSlotsCount < allSlots.length;

    if (hasMore) {
      loadMoreBtn.style.display = 'inline-block';
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = loadMoreBtn.getAttribute('data-text') || 'LOAD MORE';
    } else {
      if (allSlots.length > 0) {
        loadMoreBtn.style.display = 'inline-block';
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'No more departures scheduled';
      } else {
        loadMoreBtn.style.display = 'none';
      }
    }
  }

  function updateContinuation(route) {
    if (!continuationEl) return;

    var cfg = scheduleData && scheduleData.continuation;
    if (!cfg || !cfg.enabled || allSlots.length === 0 || !route) {
      continuationEl.hidden = true;
      return;
    }

    continuationEl.hidden = false;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
